import { useState } from 'react';
import { Select, Tooltip } from 'antd';
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { CustomRegisterOptions } from '../../types';
import { cn } from '../../utils';
import { SelectAllOption } from '../../enums/common';

interface MultiSelectInputProps<T extends FieldValues> {
  name: Path<T>;
  control?: UseFormReturn<T>['control'];
  form?: UseFormReturn<T>;
  rules?: CustomRegisterOptions<T>;
  label?: string;
  formState?: UseFormReturn<T>['formState'];
  addOnClass?: string;
  config?: Record<string, any>;
  options: { label: string; value: string | number }[];
  allSelectable?: SelectAllOption;
  maxLabelLength?: number;
}

const MultiSelectInput = <T extends FieldValues>({
  name,
  control,
  form,
  rules,
  label,
  formState,
  addOnClass = '',
  maxLabelLength,
  allSelectable,
  config = {},
  options,
}: MultiSelectInputProps<T>) => {
  const effectiveControl = control || form?.control;
  const effectiveFormState = formState || form?.formState;
  const errors = (effectiveFormState?.errors || {}) as Record<string, any>;

  if (!name || !effectiveControl) {
    return null;
  }

  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAllChange = (
    checked: boolean,
    onChange: (value: (string | number)[]) => void,
  ) => {
    setSelectAll(checked);
    onChange(checked ? options.map(option => option.value) : []);
  };

  config = {
    mode: 'multiple',
    allowClear: true,
    maxTagCount: 2,
    placeholder: 'Select options',
    showSearch: true,
    maxTagPlaceholder: (omittedValues: { label: string }[]) => (
      <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
        <span>+{omittedValues.length}</span>
      </Tooltip>
    ),
    ...config,
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-xs font-medium text-text-100 whitespace-nowrap">
          {label}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <Select
            className={addOnClass}
            {...config}
            value={value}
            onChange={newValue => {
              if (newValue.includes(-1)) {
                handleSelectAllChange(true, onChange);
              } else {
                const allSelected = options.every(option =>
                  newValue.includes(option.value),
                );
                if (allSelected) {
                  setSelectAll(true);
                } else {
                  setSelectAll(false);
                }
                onChange(newValue.filter((v: string) => v !== 'selectAll'));
              }
            }}
            onBlur={onBlur}
            ref={ref}
          >
            {allSelectable === SelectAllOption.ENABLE &&
              (options.length > 0 ? (
                <Select.Option value={-1} disabled={selectAll}>
                  Select All
                </Select.Option>
              ) : (
                <></>
              ))}

            {options.map(option => (
              <Select.Option
                key={option.value}
                label={
                  maxLabelLength
                    ? `${option.label}`.length <= maxLabelLength
                      ? `${option.label}`
                      : `${`${option.label}`.substring(0, maxLabelLength)}...`
                    : option.label
                }
                value={option.value}
              >
                {option.label}
              </Select.Option>
            ))}
          </Select>
        )}
      />
      <div
        className={cn(
          'text-xs text-red-500',
          { visible: errors[name] },
          { invisible: !errors[name] },
        )}
      >
        {errors[name] && `${errors[name]?.message}`}
      </div>
    </div>
  );
};

export default MultiSelectInput;
